import moment, { Moment } from 'moment';

const splitGroups = (offers: Array<Offer>): Array<OfferGroup> => {
  return offers.map((offer) => {
    const attendees = offer.attendees;
    const findStatus = (inv) => {
      const claim = attendees.find((at) => at.model.user_id === inv.user_id);
      return claim ? claim.status || inv.status : inv.status;
    };
    const groups = offer.groups
      ? offer.groups.map((group) => ({
          ...group,
          invitees: group.invitees.map((inv) => ({
            ...inv,
            status: findStatus(inv),
            claim: attendees.find((at) => at.model.user_id === inv.user_id),
          })),
          owner: {
            ...group.owner,
            status: findStatus(group.owner),
            claim: attendees.find((at) => at.model.user_id === group.owner.user_id),
          },
        }))
      : undefined;
    const offer2 = { ...offer, groups };

    return { offer: offer2, parts: { type: 'group_offer', offer: offer2 } as GroupOffer };
  });
};

const sortFunction = (desc: boolean) => (a: OfferGroup, b: OfferGroup) => {
  const resvs = ['reservation_slot', 'reservation_offer'];

  if (resvs.includes(a.parts.type) && resvs.includes(a.parts.type)) {
    if (
      a.parts.type !== b.parts.type &&
      moment(a.offer.starts_at).format('MM-DD-YYYY') ===
        moment(b.offer.starts_at).format('MM-DD-YYYY')
    ) {
      return resvs.indexOf(a.parts.type) > resvs.indexOf(b.parts.type) ? -1 : 1;
    }
  }

  return (moment(a.offer.starts_at).isAfter(b.offer.starts_at) ? 1 : -1) * (desc ? -1 : 1);
};

const sortOffers = (offers: Array<OfferGroup>, desc: boolean) => {
  const sortOfferFunction = (a: Offer, b: Offer) => {
    return moment(a.starts_at).isAfter(b.starts_at) ? 1 : -1;
  };
  return offers
    .map((group) => {
      if (group.parts.type === 'normal') {
        group.parts.offers.sort(sortOfferFunction);
      }
      return group;
    })
    .sort(sortFunction(desc));
};

declare interface OfferGroupDict {
  [key: string]: { offer: Offer; parts: NormalOffer };
}

const groupOffersByStuff = (offers: Array<Offer>): Array<OfferGroup> => {
  const offerKey = (offer: Offer) =>
    moment(offer.starts_at).format('MMM-DD') + (offer.template_id ?? offer.subtitle);
  const offerReduce = (value: OfferGroupDict, x: Offer): OfferGroupDict => {
    const key = offerKey(x);
    if (!value[key]) {
      value[key] = { offer: x, parts: { type: 'normal', offers: [x] } };
    } else {
      const tmp: NormalOffer = value[key].parts;
      value[key] = {
        offer: x,
        parts: { type: 'normal', offers: [...tmp.offers, x] },
      };
    }
    return value;
  };
  return Object.values(offers.reduce(offerReduce, {} as any));
};

const correctId = (offer: Offer): Offer => {
  return { ...offer, id: offer.id || offer.template_key };
};

const getDisplayDate = (attendee: Attendee) => {
  if (attendee.reserved_for) return attendee.reserved_for;
  else if (attendee.checked_in_at) return attendee.checked_in_at;
  else if (attendee.canceled_at) return attendee.canceled_at;
  else if (attendee.expires_at) return attendee.expires_at;
  else return attendee.expires_at;
};

const formatReservationRequiredOffers = (offers: Offer[], type: OfferListType) => {
  const reservations = offers.flatMap(
    (offer) =>
      offer.attendees?.map((attendee) => ({
        attendee: { ...attendee, display_date: getDisplayDate(attendee) },
        offer,
      })) || []
  );

  const reservationsByDate: { [date: string]: typeof reservations } = {};
  const offersAwaitingReservation = {
    offer: {
      type: 'reservation_placeholder',
      starts_at: moment().add(1, 'day').endOf('day').format(),
      title: 'Awaiting reservations',
    },
    parts: { type: 'reservation_slot', list: [] as { offer: Offer; attendee: Attendee }[] },
  };

  for (let i = 0; i < reservations.length; i++) {
    const reservation = reservations[i];
    if (
      reservation.offer.mode === 'apply' &&
      (reservation.attendee.applicant_status !== 'selected' ||
        !reservation.attendee.applicant_status_locked_at)
    )
      continue;
    if (
      !reservation.attendee.reserved_for &&
      type === 'active' &&
      (reservation.attendee.status === 'active' || reservation.attendee.status === 'incomplete')
    ) {
      offersAwaitingReservation.parts.list.push(reservation);
      continue;
    }
    if (
      type === 'active' &&
      reservation.attendee.reserved_for &&
      moment(reservation.attendee.reserved_for).diff(moment(), 'hours') > 24
    )
      continue;

    if (reservation.attendee.status === 'expired' && type === 'upcoming') continue;
    reservation.attendee.display_date = getDisplayDate(reservations[i].attendee);
    const date = moment(reservations[i].attendee.display_date).endOf('day').format();
    if (reservationsByDate[date]) reservationsByDate[date].push(reservation);
    else reservationsByDate[date] = [reservation];
  }

  const formattedOffers = Object.keys(reservationsByDate).map((date) => {
    return {
      offer: {
        type: 'reservation_placeholder',
        starts_at: date,
        title: moment(date).format('MMM D'),
      },
      parts: {
        type: 'reservation_slot',
        list: reservationsByDate[date].sort((a, b) => {
          if (type == 'past') return moment(b.attendee.display_date).diff(a.attendee.display_date);
          return moment(a.attendee.display_date).diff(b.attendee.display_date);
        }),
      },
    };
  });

  return ((type === 'active' && offersAwaitingReservation.parts.list.length > 0
    ? [offersAwaitingReservation, ...formattedOffers]
    : formattedOffers) as any) as OfferGroup[];
};

const getUniqRROffers = (offers: Offer[], type: OfferListType) => {
  const ids: string[] = [];
  const newOffers: Offer[] = [];
  for (let i = 0; i < offers.length; i++) {
    const offer = offers[i];
    if (
      ids.includes(offer.template_id) ||
      offer.type !== 'reservation_required' ||
      offer.mode === 'apply'
    )
      continue;
    if (type === 'upcoming' && offer.state !== 'pending') continue;
    newOffers.push(offer);
    ids.push(offer.template_id);
  }
  return newOffers;
};

const formatOffers = (offers: Array<Offer>, type: OfferListType): Array<OfferGroup> => {
  const idOffers = offers.map(correctId);
  const normalOffers = idOffers.filter(
    (offer) =>
      (offer.type !== 'reservation_required' ||
        (offer.type === 'reservation_required' && offer.mode === 'apply')) &&
      !offer.is_group
  );
  const groupOffers = idOffers.filter((offer) => offer.is_group);

  const reservationOffers = idOffers.filter((offer) => offer.type === 'reservation_required');
  const emptyreservationOffers = getUniqRROffers(idOffers, type);
  const formattedReservationOffers = formatReservationRequiredOffers(reservationOffers, type);

  const sortedOffers = sortOffers(
    [
      ...groupOffersByStuff([...normalOffers, ...emptyreservationOffers]),
      ...splitGroups(groupOffers),
      ...formattedReservationOffers,
    ],
    type === 'past'
  );

  return sortedOffers;
};

const setNewOfferTimes = (start_time: Moment, end_time: Moment, now: Moment) => {
  let start = start_time.clone();
  let end = end_time.clone();
  if (start_time.isAfter(now) && end_time.isAfter(now)) {
    //nothing
  } else if (start_time.isBefore(now) && end_time.isAfter(now)) {
    start = start.add(1, 'day');
    end = end.add(1, 'day');
  } else {
    start = start.set({ date: now.date(), month: now.month(), year: now.year() });
    end = end.set({ date: now.date(), month: now.month(), year: now.year() });
    while (start.isBefore(now) && end.isBefore(now)) {
      start.add(1, 'day');
      end.add(1, 'day');
    }
  }
  return { starts_at: start.format(), ends_at: end.format() };
};

export { formatOffers, correctId, setNewOfferTimes };
